import { EProjectType, ESdChangeReason } from "@kuika/kuika-cl-model";
import { IWithSDProps, withSD } from "../../../../shared/hoc/screen-design-consumer";
import { IWithContextProp, withContext } from "../../../../shared/hoc/with-context";
import { PureSdComponent } from "../../../../shared/pure-sd";
import { PropertyHelper } from "../../../../shared/utility/property-helper";
import KuikaColorPicker from "../../../ui-kit/kuika-color-picker/kuika-color-picker";
import KuikaInput from "../../../ui-kit/kuika-input/kuika-input";
import KuikaSelectbox from "../../../ui-kit/kuika-selectbox/kuika-selectbox";
import KuikaSmallMediumText from "../../../ui-kit/typography/SmallMediumText/SmallMediumText";

/** Swatch fallbacks — must mirror the code generator's defaults so the picker
 * never shows a colour the generated app would not actually use. Read with `||`, not
 * `??`: an edit to any one field writes the others back as "" when unset, and "" means
 * "use the default", not "no colour". */
const DEFAULT_ICON_BACKGROUND_COLOR: string = "#246dff";
const DEFAULT_CONFIRM_BUTTON_COLOR: string = "#246eff";
const DEFAULT_CANCEL_BUTTON_COLOR: string = "#246eff";

export interface IActionConfirmationPickerState {}

/** Fields changed by a single edit; everything else keeps its current value. */
export interface IActionConfirmationOverrides {
  confirmationMessage?: string;
  confirmationFontFamily?: string;
  confirmationHeader?: string;
  confirmationIcon?: string;
  confirmationIconBackgroundColor?: string;
  confirmationConfirmButtonColor?: string;
  confirmationCancelButtonColor?: string;
}

export interface IActionConfirmationPickerProps {
  confirmationMessage?: string;
  confirmationFontFamily?: string;
  confirmationHeader?: string;
  confirmationIcon?: string;
  confirmationIconBackgroundColor?: string;
  confirmationConfirmButtonColor?: string;
  confirmationCancelButtonColor?: string;
  onChange?: (
    confirmationMessage: string,
    confirmationFontFamily: string,
    confirmationHeader: string,
    confirmationIcon: string,
    confirmationIconBackgroundColor: string,
    confirmationConfirmButtonColor: string,
    confirmationCancelButtonColor: string
  ) => void;
}

class ActionConfirmation extends PureSdComponent<
  IActionConfirmationPickerProps & IWithSDProps & IWithContextProp,
  IActionConfirmationPickerState
> {
  constructor(props: IActionConfirmationPickerProps & IWithSDProps & IWithContextProp) {
    super(props);
  }

  /** Single funnel to onChange so the seven positional arguments are built in
   * exactly one place; each editor only names the field it owns. */
  emitChange = (overrides: IActionConfirmationOverrides): void => {
    this.props.onChange?.(
      overrides.confirmationMessage ?? this.props.confirmationMessage ?? "",
      overrides.confirmationFontFamily ?? this.props.confirmationFontFamily ?? "",
      overrides.confirmationHeader ?? this.props.confirmationHeader ?? "",
      overrides.confirmationIcon ?? this.props.confirmationIcon ?? "",
      overrides.confirmationIconBackgroundColor ?? this.props.confirmationIconBackgroundColor ?? "",
      overrides.confirmationConfirmButtonColor ?? this.props.confirmationConfirmButtonColor ?? "",
      overrides.confirmationCancelButtonColor ?? this.props.confirmationCancelButtonColor ?? ""
    );
  };

  handleBlurMessage = (event: any) => {
    const value = PropertyHelper.getTextValueForMetaData(event.target.value);
    this.emitChange({ confirmationMessage: value ?? "" });
  };

  handleBlurHeader = (event: any) => {
    const value = PropertyHelper.getTextValueForMetaData(event.target.value);
    this.emitChange({ confirmationHeader: value ?? "" });
  };

  handleIconChange = (value: string) => {
    this.emitChange({ confirmationIcon: value });
  };

  handleIconBackgroundColorChange = (color: string) => {
    this.emitChange({ confirmationIconBackgroundColor: color });
  };

  handleConfirmButtonColorChange = (color: string) => {
    this.emitChange({ confirmationConfirmButtonColor: color });
  };

  handleCancelButtonColorChange = (color: string) => {
    this.emitChange({ confirmationCancelButtonColor: color });
  };

  render() {
    return (
      <>
        <div className="kuika-flex-column" style={{ marginTop: 8, marginBottom: 4 }}>
          <KuikaSmallMediumText>Confirmation Message</KuikaSmallMediumText>
        </div>
        <div className="kuika-flex-column" style={{ marginBottom: 8 }}>
          <div style={{ width: "100%" }}>
            <KuikaInput
              style={{ width: "100%", height: "auto" }}
              className="kuika_multiline_property kuika_confirmation_property"
              defaultValue={PropertyHelper.getTextValueForDisplay(this.props.confirmationMessage ?? "")}
              onBlur={this.handleBlurMessage}
              rows={3}
              type="textarea"
              resizable={true}
              data-testid="confirmationMessageTextarea"
            />
          </div>
        </div>
        {this.props.sd.project.projectType === EProjectType.Desktop && (
          <>
            <div className="kuika-flex-column" style={{ marginTop: 8, marginBottom: 4 }}>
              <KuikaSmallMediumText>Confirmation Font Family</KuikaSmallMediumText>
            </div>
            <div className="kuika-flex-column" style={{ marginBottom: 8 }}>
              <div style={{ width: "100%" }}>
                <KuikaSelectbox
                  style={{ width: "100%" }}
                  defaultValue={this.props.confirmationFontFamily ?? "Poppins"}
                  onChange={(value) => {
                    this.emitChange({ confirmationFontFamily: value as string });
                  }}
                  options={this.props.sd?.project?.componentSetObj?.fontNames.map((font) => ({
                    label: font,
                    value: font
                  }))}
                  data-testid="confirmationFontFamilyDropdown"
                />
              </div>
              <div className="kuika-flex-column" style={{ marginTop: 8, marginBottom: 4 }}>
                <KuikaSmallMediumText>Confirmation Header</KuikaSmallMediumText>
              </div>
              <div className="kuika-flex-column" style={{ marginBottom: 8 }}>
                <KuikaInput
                  style={{ width: "100%" }}
                  defaultValue={PropertyHelper.getTextValueForDisplay(this.props.confirmationHeader ?? "")}
                  onBlur={this.handleBlurHeader}
                  type="textarea"
                />
              </div>
            </div>
            <div className="kuika-flex-column" style={{ marginTop: 8, marginBottom: 4 }}>
              <KuikaSmallMediumText>Confirmation Icon</KuikaSmallMediumText>
            </div>
            <div className="kuika-flex-column" style={{ marginBottom: 8 }}>
              <KuikaSelectbox
                style={{ width: "100%" }}
                defaultValue={this.props.confirmationIcon ?? ""}
                value={this.props.confirmationIcon ?? ""}
                onChange={(value) => this.handleIconChange(value as string)}
                options={[
                  { label: "Confirmation", value: "confirmation" },
                  { label: "Warning", value: "warning" },
                  { label: "Trash", value: "trash" },
                  { label: "Info", value: "info" },
                  { label: "Question", value: "question" },
                  { label: "Notification", value: "notification" }
                ]}
                allowClear
                onClear={() => this.handleIconChange("")}
              />
            </div>
            <div className="kuika-flex-column" style={{ marginTop: 8, marginBottom: 4 }}>
              <KuikaSmallMediumText>Confirmation Icon Background Color</KuikaSmallMediumText>
            </div>
            <div className="kuika-flex-column" style={{ marginBottom: 8 }}>
              <KuikaColorPicker
                color={this.props.confirmationIconBackgroundColor || DEFAULT_ICON_BACKGROUND_COLOR}
                onChange={(color: string): void => this.handleIconBackgroundColorChange(color)}
                dataTestId="confirmationIconBackgroundColorPicker"
              />
            </div>
            <div className="kuika-flex-column" style={{ marginTop: 8, marginBottom: 4 }}>
              <KuikaSmallMediumText>Confirm Button Color</KuikaSmallMediumText>
            </div>
            <div className="kuika-flex-column" style={{ marginBottom: 8 }}>
              <KuikaColorPicker
                color={this.props.confirmationConfirmButtonColor || DEFAULT_CONFIRM_BUTTON_COLOR}
                onChange={(color: string): void => this.handleConfirmButtonColorChange(color)}
                dataTestId="confirmationConfirmButtonColorPicker"
              />
            </div>
            <div className="kuika-flex-column" style={{ marginTop: 8, marginBottom: 4 }}>
              <KuikaSmallMediumText>Cancel Button Color</KuikaSmallMediumText>
            </div>
            <div className="kuika-flex-column" style={{ marginBottom: 8 }}>
              <KuikaColorPicker
                color={this.props.confirmationCancelButtonColor || DEFAULT_CANCEL_BUTTON_COLOR}
                onChange={(color: string): void => this.handleCancelButtonColorChange(color)}
                dataTestId="confirmationCancelButtonColorPicker"
              />
            </div>
          </>
        )}
      </>
    );
  }

  checkNeedRender = (changeReason: ESdChangeReason): boolean => {
    if (
      changeReason === ESdChangeReason.ChangeSelectedDiByCanvasFrame ||
      changeReason === ESdChangeReason.ChangeSelectedDiByTools ||
      changeReason === ESdChangeReason.DragDropDi ||
      changeReason === ESdChangeReason.DragDropBlock ||
      changeReason === ESdChangeReason.WFStepMappingChange
    ) {
      return true;
    }
    return false;
  };
}

const actionConfirmation = withContext<IActionConfirmationPickerProps>(
  withSD<IActionConfirmationPickerProps>(ActionConfirmation)
);
export { actionConfirmation as ActionConfirmation };
